import { IconButton, TextField, Typography } from "@material-ui/core";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import DeleteForeverIcon from "@material-ui/icons/DeleteForever";
import { useTheme } from "@material-ui/styles";
import ColorButton from "components/common/ColorButton";
import ContentTitle from "components/common/ContentTitle";
import ButtonController from "components/request-document/components/ButtonController";
import {
  StyledTableCell,
  useStyles,
} from "components/request-document/components/Commons";
import { useFormik } from "formik";
import { SMART_T_STEP } from "libs/constant/Step";
import useModalLoading from "libs/hooks/useModalLoading";
import {
  defaultJobTable,
  getDefaultRow,
  getErrorMessage,
  isError,
  mapJobTable,
} from "libs/utils/form";
import { get } from "lodash-es";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getMasterData, selectMasterData } from "redux/modules/masterSlice";
import { selectPersonalDetail } from "redux/modules/personalSlice";
import {
  selectIsChange,
  selectTypeChange,
  setIsChange,
  setSmartFormStep,
  setTypeChange,
} from "redux/modules/uiSlice";
import {
  addSmartForm,
  addSmartFormSlice,
  getSmartForm,
  getSmartFormFieldConfig,
  selectAddSmartForm,
  selectCurrentDocument,
  selectCurrentSmartForm,
  selectCurrentSmartServiceId,
  selectDraftId,
  selectSmartFormFieldConfig,
} from "redux/modules/visaForm";
import * as yup from "yup";

export default function AddJobPositionStep() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const theme = useTheme();
  const { showLoading, hideLoading } = useModalLoading();
  const [isSaveDraft, setSaveDraft] = useState(false);

  const personalDetail = useSelector(selectPersonalDetail);
  const smartServiceId = useSelector(selectCurrentSmartServiceId);
  const draftId = useSelector(selectDraftId);
  const masterData = useSelector(selectMasterData);
  const smartFormFieldConfig = useSelector(selectSmartFormFieldConfig);
  const addSmartFormState = useSelector(selectAddSmartForm);
  const smartform = useSelector(selectCurrentSmartForm);
  const currentDocument = useSelector(selectCurrentDocument);
  const isChange = useSelector(selectIsChange);
  const typeChange = useSelector(selectTypeChange);

  const smartNameById = (p, c) => ({ ...p, [c.smartName]: c });

  const personalId = get(personalDetail, "personalID", "");
  const jobPositionConfig = get(masterData, "jobPositionConfig", []);

  const detailList = ["ConditionType", "JobTable"];
  const detail = get(smartform, "detail", [])
    .filter((field) => detailList.includes(field.smartName))
    .reduce(smartNameById, {});

  useEffect(() => {
    dispatch(
      getMasterData({
        JobPositionConfig: true,
        CountryConfig: true,
      })
    );
  }, [dispatch]);

  useEffect(() => {
    if (smartServiceId) {
      dispatch(getSmartFormFieldConfig({ smartserviceId: smartServiceId }));
      dispatch(
        getSmartForm({
          smartserviceId: smartServiceId,
          draftId,
          personalId,
        })
      );
    }
  }, [dispatch, draftId, personalId, smartServiceId]);

  useEffect(() => {
    if (!addSmartFormState.isLoading) {
      if (addSmartFormState.isSuccess) {
        hideLoading();
        dispatch(addSmartFormSlice.actions.clearAsyncState());
        dispatch(
          getSmartForm({
            smartserviceId: smartServiceId,
            draftId,
            personalId,
          })
        );
        if (isChange) {
          dispatch(setIsChange(false));
          dispatch(setTypeChange(""));
        }
        if (!isSaveDraft) {
          dispatch(setSmartFormStep(SMART_T_STEP.UPLOAD_DOCUMENT));
        }
      } else if (addSmartFormState.isFailure) {
        hideLoading();
        alert(addSmartFormState.error?.message);
        dispatch(addSmartFormSlice.actions.clearAsyncState());
      }
    }
  }, [addSmartFormState, dispatch, hideLoading, isSaveDraft]);

  const callAPISubmit = async (values) => {
    showLoading();

    const defaultType = get(smartFormFieldConfig, "data.defaultType", []);
    const documentFields = Object.keys(values);
    const getFieldListConfig = defaultType.filter((field) =>
      documentFields.includes(field.smartName)
    );

    const getValue = (field) => {
      switch (field.smartName) {
        case "JobTable": {
          const jobTable = get(values, field.smartName, []);
          const value = jobTable.filter((v) => !!v.jobTitle);
          return value || null;
        }
        default:
          return get(values, `${field.smartName}`, null);
      }
    };

    const getSmartId = (field) =>
      get(detail, `${field.smartName}.smartId`, null);

    const prepareData = {
      draftId: draftId || null,
      smartserviceId: smartServiceId,
      personalId: personalId,
      survey: null,
      visaissuance: null,
      detail: [
        ...getFieldListConfig.map((field) => ({
          smartName: field.smartName,
          smartId: getSmartId(field),
          partfromId: field.partfromId,
          configsmartId: field.configsmartId,
          linkTable: field.linkTable,
          value: getValue(field),
        })),
      ],
    };

    await dispatch(
      addSmartForm({
        data: prepareData,
      })
    );
  };

  const validationSchema = yup.object({
    // ContactEmail: yup.string().email("Invalid email format"),
  });
  const formik = useFormik({
    enableReinitialize: true,
    validationSchema,
    initialValues: {
      JobTable: getDefaultRow(detail, "JobTable.value", defaultJobTable).map(
        mapJobTable
      ),
    },
    onSubmit: async (values) => {
      setSaveDraft(false);
      callAPISubmit(values);
    },
  });

  useEffect(() => {
    if (currentDocument.isSuccess && isChange && typeChange) {
      setSaveDraft(true);
      callAPISubmit({ ConditionType: typeChange });
    }
  }, [currentDocument.isSuccess, isChange, typeChange]);

  const JobTable = formik.values.JobTable;

  return (
    <form onSubmit={formik.handleSubmit}>
      <ContentTitle
        title="Add Job Position"
        subTitle="Smart Visa Qualifications Update Form"
      />

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <ColorButton
          variant="contained"
          color="info"
          size="small"
          onClick={() => {
            formik.setFieldValue("JobTable", [
              ...JobTable,
              mapJobTable(defaultJobTable),
            ]);
          }}
        >
          <Typography style={{ fontWeight: "bold" }} variant="button">
            + Add Job
          </Typography>
        </ColorButton>
      </div>
      <TableContainer style={{ marginTop: 35, borderRadius: 4 }}>
        {JobTable.map((jobTable, index) => {
          const jobSubPositionConfig =
            jobPositionConfig.find((job) => job.jobID === jobTable.jobID)
              ?.jobSubPosition || [];

          if (!jobTable.Status) return null;
          return (
            <Table
              key={index}
              className={classes.table}
              style={{ marginBottom: 50 }}
            >
              <TableHead>
                <TableRow>
                  <StyledTableCell colSpan={6}>
                    <div
                      className={classes.flexRow}
                      style={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography
                        variant="h6"
                        style={{ fontWeight: "bold" }}
                        className={classes.title}
                      >
                        Job Position
                      </Typography>
                      {index === 0 ? (
                        <div />
                      ) : (
                        <IconButton
                          style={{
                            backgroundColor: theme.palette.common.white,
                            color: theme.palette.error.main,
                          }}
                          size="medium"
                          onClick={async () => {
                            if (
                              get(
                                formik.values,
                                `JobTable.${index}.jobpositionID`,
                                ""
                              )
                            ) {
                              formik.setFieldValue(
                                `JobTable.${index}.Status`,
                                false
                              );
                            } else {
                              formik.setFieldValue(
                                "JobTable",
                                JobTable.filter((__, i) => i !== index)
                              );
                            }
                          }}
                        >
                          <DeleteForeverIcon />
                        </IconButton>
                      )}
                    </div>
                  </StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <StyledTableCell colSpan={6}>
                    <div className={classes.cellContent}>
                      <div className={classes.flexRow}>
                        <InputLabel>Job position</InputLabel>
                        <FormControl
                          variant="outlined"
                          className={classes.formControl}
                          style={{ marginLeft: 16, width: 300 }}
                          fullWidth
                        >
                          <InputLabel>Selected Job position</InputLabel>
                          <Select
                            label="Selected Job position"
                            name={`JobTable.${index}.jobID`}
                            defaultValue=""
                            value={get(
                              formik.values,
                              `JobTable.${index}.jobID`,
                              ""
                            )}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            fullWidth
                          >
                            {jobPositionConfig.map((jobPosition) => (
                              <MenuItem
                                key={jobPosition.jobID}
                                value={jobPosition.jobID}
                              >
                                {jobPosition.jobName}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <FormControl
                          variant="outlined"
                          className={classes.formControl}
                          style={{ marginLeft: 16, width: 300 }}
                          fullWidth
                        >
                          <InputLabel>Selected Job Sub position</InputLabel>
                          <Select
                            label="Selected Job Sub position"
                            name={`JobTable.${index}.jobsubID`}
                            defaultValue=""
                            value={get(
                              formik.values,
                              `JobTable.${index}.jobsubID`,
                              ""
                            )}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            fullWidth
                          >
                            {jobSubPositionConfig.map((jobPosition) => (
                              <MenuItem
                                key={jobPosition.jobsubID}
                                value={jobPosition.jobsubID}
                              >
                                {jobPosition.jobsubName}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </div>
                    </div>
                  </StyledTableCell>
                </TableRow>

                <TableRow>
                  <StyledTableCell colSpan={6}>
                    <div className={classes.cellContent}>
                      <div
                        className={classes.flexRow}
                        style={{ width: "100%" }}
                      >
                        <InputLabel style={{ width: 90 }}>Job Title</InputLabel>
                        <TextField
                          variant="outlined"
                          className={classes.textField}
                          name={`JobTable.${index}.jobTitle`}
                          value={get(
                            formik.values,
                            `JobTable.${index}.jobTitle`,
                            ""
                          )}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={isError(`JobTable.${index}.jobTitle`, formik)}
                          helperText={getErrorMessage(
                            `JobTable.${index}.jobTitle`,
                            formik
                          )}
                        />
                      </div>
                    </div>
                  </StyledTableCell>
                </TableRow>

                <TableRow>
                  <StyledTableCell colSpan={6}>
                    <div className={classes.cellContent}>
                      <div className={classes.wrapField}>
                        <InputLabel>Job description</InputLabel>
                        <TextField
                          variant="outlined"
                          className={classes.textField}
                          name={`JobTable.${index}.jobDesc`}
                          value={get(
                            formik.values,
                            `JobTable.${index}.jobDesc`,
                            ""
                          )}
                          error={isError(`JobTable.${index}.jobDesc`, formik)}
                          helperText={getErrorMessage(
                            `JobTable.${index}.jobDesc`,
                            formik
                          )}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          multiline
                          rows={5}
                        />
                      </div>
                    </div>
                  </StyledTableCell>
                </TableRow>
              </TableBody>
            </Table>
          );
        })}
      </TableContainer>

      <ButtonController
        onClickSaveDraft={() => {
          setSaveDraft(true);
          callAPISubmit(formik.values);
        }}
      />
    </form>
  );
}
